// The signed-in student: are they onboarded, who are they, and what can they
// spend.
//
// ── Why this is a shared store and not a plain per-component fetch ──────────
//
// `onboardingStatus` and `studentProfile` come back TOGETHER (documents.js,
// BOOT_QUERY) because the boot needs both in the same frame: one decides
// whether the app renders the form or the router, the other paints the rail.
// The profile and the balance are then read by four more places — the rail, the
// chat composer (which must know whether a question is affordable BEFORE it is
// pressed), the credits screen and the profile screen — and the balance CHANGES
// underneath all of them every time an AI action runs.
//
// A hook that fetched per mount would give those surfaces different numbers and
// as many round trips, and the one in the rail would be the stalest. So there is
// exactly one copy in this module, every `useMe()` subscribes to it, and a
// mutation that moved the balance calls `refreshBalance()` — which repaints all
// of them at once.
//
// THE WALLET IS THE ONLY AUTHORITY on that number (core.md §3.5). Nothing here
// ever derives a balance by summing `CaseChatMessage.costParseh`: the AI
// service's credit check FAILS OPEN, so a message can legitimately carry a cost
// the ledger has no row for. `costParseh` describes the price list; this
// describes the wallet.
//
// ── The onboarding verdict, and the one shape it must not have ────────────
//
// v1 had no onboarding at all and the BFF gated on a `student` role nobody
// granted — the circularity core.md §4.2 removes. v2's gate is a stored
// profile, so the boot has to answer "is there one?" before it renders
// anything.
//
// The answer comes from `onboardingStatus`, which is a ROOT THE GATE ALWAYS
// ALLOWS, and from nothing else. In particular a FAILED boot is never read as
// "not onboarded": the doctor app once read a stale token that way, showed its
// registration wizard to an already-registered doctor, and produced a duplicate
// account. Here a failure is an OUTAGE — `error` is published, `status` stays
// null, and App.jsx renders a full-page error with a retry rather than a form
// that would save into a store that is not answering (core.md §4.3).

import React from 'react';

import { bffQuery, bffMutation, errorCode } from '../lib/bffClient.js';
import { refreshAfterOnboarding } from '../lib/auth.js';
import {
  BOOT_QUERY,
  STUDENT_PROFILE_QUERY,
  WALLET_BALANCE_QUERY,
  COMPLETE_ONBOARDING_MUTATION,
  UPDATE_PROFILE_MUTATION,
} from '../graphql/documents.js';

// ── The store ───────────────────────────────────────────────────────────────

const EMPTY = { status: null, me: null, balance: null, loading: true, error: null };

let state = EMPTY;
const listeners = new Set();

function publish(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn(state));
}

// One in-flight boot shared by every caller. App.jsx's gate and the surfaces
// that read this store all want the same facts in the same frame; without this
// they would issue five identical requests — and could end up holding different
// answers if one of them failed.
let bootInFlight = null;

/**
 * Read `onboardingStatus` + `studentProfile` into the store.
 *
 * REJECTS as well as recording, which is the one thing the subscribers and the
 * boot gate need differently: a subscriber only wants the store to say "error",
 * while App.jsx has to branch on the CODE — UNAUTHENTICATED sends the student
 * to the sign-in screen, anything else is an outage with a retry.
 *
 * `NOT_ONBOARDED` cannot arrive here: both roots in the boot query are on the
 * gate's allow-list (core.md §4.2). If it ever does, it is an outage like any
 * other and the form is NOT shown — a form is shown because the status said
 * `isOnboarded: false`, never because a query failed.
 */
export function bootIdentity() {
  if (bootInFlight) return bootInFlight;
  publish({ loading: true });
  bootInFlight = bffQuery(BOOT_QUERY)
    .then((data) => {
      publish({
        status: data?.onboardingStatus || null,
        me: data?.studentProfile || null,
        // The boot deliberately does NOT ask the wallet: `myWalletBalance` is
        // gated on the profile, and a field-level NOT_ONBOARDED beside a
        // perfectly good `onboardingStatus` would make the whole boot look like
        // an error. `studentProfile.balance` is the same number by contract.
        balance: data?.studentProfile?.balance ?? null,
        loading: false,
        error: null,
      });
    })
    .catch((err) => {
      console.error('[useMe] boot failed:', errorCode(err) || err?.message);
      publish({ loading: false, error: err });
      throw err;
    })
    .finally(() => {
      bootInFlight = null;
    });
  return bootInFlight;
}

/** Is there a stored profile? `null` while the boot has not answered. */
export function isOnboarded(snapshot = state) {
  if (snapshot.status) return Boolean(snapshot.status.isOnboarded);
  if (snapshot.me) return true;
  return snapshot.loading || snapshot.error ? null : false;
}

/**
 * Save the onboarding form, then take the token's word for it once.
 *
 * The BFF grants the `student` client role after the upsert, NON-FATALLY: the
 * role is informational today (no service authorizes on it) and the operator
 * may not have created it yet (core.md §4.1, A1). The refresh below is what
 * puts it into `resource_access` for whoever reads it later — and it is
 * deliberately best-effort for the same reason. A student whose refresh failed
 * is onboarded: the PROFILE is the gate, not the role.
 */
export async function completeOnboarding(input) {
  const data = await bffMutation(COMPLETE_ONBOARDING_MUTATION, { input });
  const profile = data?.completeStudentOnboarding || null;
  publish({
    me: profile,
    status: { ...(state.status || {}), isOnboarded: true },
    balance: profile?.balance ?? state.balance,
    error: null,
  });
  try {
    await refreshAfterOnboarding();
  } catch (err) {
    // Not surfaced: the profile is stored and the app is usable. Turning a
    // failed token refresh into an error over a completed registration would
    // send the student back to a form they have already submitted — which is
    // how a duplicate account gets made.
    console.warn('[useMe] token refresh after onboarding failed:', errorCode(err) || err?.message);
  }
  return profile;
}

/** Edit the profile from `/profile`. Omitted = untouched, `""` clears. */
export async function updateProfile(input) {
  const data = await bffMutation(UPDATE_PROFILE_MUTATION, { input });
  const profile = data?.updateStudentProfile || null;
  if (profile) publish({ me: profile, balance: profile.balance ?? state.balance });
  return profile;
}

/**
 * Re-read the balance ALONE, after something spent or added پارسه.
 *
 * The cheap half of the boot: an AI answer does not change the student's name
 * or their patient count, and re-reading the profile for it would cost a second
 * downstream call for facts that did not move.
 *
 * Failure is swallowed on purpose. This is called in the tail of a SUCCESSFUL
 * mutation — the answer is on screen and the money is spent — so a failed
 * refresh means the badge is stale for a few seconds, and turning that into an
 * error banner over a working screen would be a lie about what failed.
 */
export function refreshBalance() {
  return bffQuery(WALLET_BALANCE_QUERY)
    .then((data) => {
      const next = data?.myWalletBalance?.balance;
      if (next != null) publish({ balance: next });
    })
    .catch((err) => {
      console.warn('[useMe] balance refresh failed:', errorCode(err) || err?.message);
    });
}

/** Re-read the PROFILE alone, after the patient count moved. */
export function refreshProfile() {
  return bffQuery(STUDENT_PROFILE_QUERY)
    .then((data) => {
      if (data?.studentProfile) publish({ me: data.studentProfile });
    })
    .catch((err) => {
      console.warn('[useMe] profile refresh failed:', errorCode(err) || err?.message);
    });
}

/**
 * Adopt a balance a mutation ALREADY returned.
 *
 * `redeemGiftCode` answers with the balance after the grant landed, so asking
 * the wallet again for a number we are holding is a round trip that can only
 * disagree with itself — and, between the two calls, an AI action could land
 * and make the later read look like the redemption granted less than it did.
 */
export function setBalance(next) {
  if (next == null) return;
  publish({ balance: Number(next) });
}

/**
 * Discard everything and boot again. The retry button behind the boot error.
 *
 * The rejection is swallowed here because the caller is a retry BUTTON, not the
 * gate: the store's `error` is what the screen re-renders from, and an
 * unhandled rejection out of an onClick is a console error nobody can act on.
 */
export function reloadIdentity() {
  state = { ...EMPTY };
  bootInFlight = null;
  return bootIdentity().catch(() => {});
}

/**
 * Discard everything and boot again — for the GATE, which needs the rejection.
 *
 * App.jsx calls this when a request came back `NOT_ONBOARDED` mid-session: the
 * profile this app booted on is gone, and the only honest next step is to ask
 * `onboardingStatus` again and let the answer decide between the router and
 * the onboarding screen. Unlike `reloadIdentity` this REJECTS, because the gate
 * branches on the code (UNAUTHENTICATED → sign-in, anything else → outage).
 */
export function rebootIdentity() {
  state = { ...EMPTY };
  bootInFlight = null;
  return bootIdentity();
}

/** Test seam — drops the cached answer and every subscriber. */
export function resetIdentity() {
  state = { ...EMPTY };
  bootInFlight = null;
  listeners.clear();
}

// ── The hook ────────────────────────────────────────────────────────────────

export function useMe() {
  const [snapshot, setSnapshot] = React.useState(state);

  React.useEffect(() => {
    listeners.add(setSnapshot);
    // Sync first: the store may have resolved between this component's render
    // and its effect, and a subscriber that only waits for the NEXT publish
    // would sit on a stale `loading: true` forever.
    setSnapshot(state);
    // Normally a no-op: App.jsx's gate has already booted this store before any
    // of these surfaces could mount. It stays because a subscriber must not
    // depend on the order it happened to mount in — and `bootIdentity` dedupes
    // an in-flight one anyway.
    if (!state.me && !state.status && !state.error) bootIdentity().catch(() => {});
    return () => {
      listeners.delete(setSnapshot);
    };
  }, []);

  return {
    me: snapshot.me,
    status: snapshot.status,
    onboarded: isOnboarded(snapshot),
    balance: snapshot.balance,
    loading: snapshot.loading,
    error: snapshot.error,
    reload: reloadIdentity,
    refreshBalance,
    refreshProfile,
    completeOnboarding,
    updateProfile,
  };
}

export default useMe;
